import { memo, useCallback, useMemo, useState } from 'react';
import { cn } from '@divlab/divanui';

import useMedias from '@Hooks/useMedias';
import TariffInformation from '../TariffInformation';
import TariffAdvantages from '../TariffAdvantages';
import TariffImage from '../TariffImage';
import TariffPrice from '../TariffPrice';
import styles from './Tariff.module.css';

import type { FC, HTMLAttributes, MouseEvent } from 'react';
import type { TariffData, TariffTabData } from '@Pages/PageExpressDesign/type';
import type { TabData } from '@Pages/PageExpressDesign/elements/Tab';

interface TariffProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  tariff: TariffData;
  onScrollToBlock?: (e: MouseEvent, anchorName: string, tariffId?: string) => void;
}

const Tariff: FC<TariffProps> = (props) => {
  const { className, tariff, onScrollToBlock, ...restProps } = props;
  const { isDesktop, isMobile } = useMedias();
  const { img, tabs, advantages, id, example } = tariff;

  const formatTabs: TabData[] = useMemo(() => {
    return tabs.map((tab) => {
      return { id: tab.id.toString(), label: tab.name, tooltip: [tab.tooltip] };
    });
  }, [tabs]);

  const [selectedTab, setSelectedTab] = useState<TariffTabData>(
    formatTabs.length > 0 ? tabs[0] : null,
  );

  const handleChangeTab = useCallback(
    (newTab: TabData) => {
      const newSelectedTab = tabs.filter((tab) => tab.id.toString() === newTab?.id)[0];
      setSelectedTab(newSelectedTab);
    },
    [tabs],
  );

  return (
    <div {...restProps} className={cn(styles.tariff, className)}>
      {isDesktop && !isMobile ? (
        <>
          <div className={styles.textWrapper}>
            <TariffInformation
              className={styles.information}
              tariff={tariff}
              tabs={formatTabs}
              selectedTab={selectedTab}
              onChangeTab={handleChangeTab}
            />
            <TariffAdvantages advantages={advantages} />
          </div>
          <div className={cn(styles.wrapper)}>
            <TariffImage className={styles.image} image={img} />
            <TariffPrice
              price={selectedTab.price}
              onScrollToBlock={onScrollToBlock}
              tariffId={id.toString()}
              example={example}
            />
          </div>
        </>
      ) : (
        <>
          <TariffImage className={styles.image} image={img} />
          <TariffInformation
            className={styles.information}
            tariff={tariff}
            tabs={formatTabs}
            selectedTab={selectedTab}
            onChangeTab={handleChangeTab}
          />
          <TariffPrice
            className={styles.price}
            price={selectedTab.price}
            onScrollToBlock={onScrollToBlock}
            tariffId={id.toString()}
            example={example}
          />
          <TariffAdvantages className={styles.advantages} advantages={advantages} />
        </>
      )}
    </div>
  );
};

export default memo(Tariff);
